import React, { useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { FileUpload, H4 } from '../../components';

import css from './ProfileDocumentsPanel.module.css';

// Documents, not media. Keeping this narrow avoids a round-trip through
// Sharetribe's verification only to be told the mime type is unsupported.
const ACCEPT_DOCUMENTS = '.pdf,.doc,.docx,.jpg,.jpeg,.png';

/**
 * A single named document slot.
 *
 * The slot is either empty, mid-upload, or holding a verified file. Sharetribe's
 * attachment list carries no slot label, so a persisted file is matched back to
 * its slot through the privateData map and then handed to the same FileUpload
 * component the live uploads use — that way both look identical to the user.
 */
const DocumentSlot = props => {
  const {
    document,
    attachedFile,
    upload,
    actionState,
    disabled,
    onSelectFile,
    onRemove,
    onDownload,
  } = props;
  const inputRef = useRef(null);

  const { key, label, isOptional, isOrphaned } = document;
  const isBusy = actionState?.inProgress;

  // A verified, persisted file is shaped into the same item FileUpload expects
  // for a just-finished upload.
  const persistedItem = attachedFile
    ? {
        tempId: key,
        uploadInProgress: false,
        verificationInProgress: false,
        progress: null,
        verificationStatus: 'available',
        file: attachedFile,
        sourceFile: null,
        error: null,
      }
    : null;

  const item = upload || persistedItem;

  const handleChange = e => {
    const file = e.target.files?.[0];
    if (file) {
      onSelectFile(file, key);
    }
    // Reset so re-picking the same filename still fires a change event.
    e.target.value = '';
  };

  const handleRemove = () => {
    // A row still in the upload map was never attached, so it only needs
    // clearing locally; anything else is persisted and needs the API call.
    onRemove(key, !!upload);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <li className={css.slot}>
      <div className={css.slotHeader}>
        <span className={css.slotLabel}>{label}</span>
        {isOrphaned ? (
          <span className={css.orphaned}>
            <FormattedMessage id="ProfileDocumentsPanel.noLongerRequired" />
          </span>
        ) : isOptional ? (
          <span className={css.optional}>
            <FormattedMessage id="ProfileDocumentsPanel.optional" />
          </span>
        ) : (
          <span className={css.required}>
            <FormattedMessage id="ProfileDocumentsPanel.required" />
          </span>
        )}
      </div>

      {item ? (
        <FileUpload
          item={item}
          onRemoveFile={handleRemove}
          onDownloadFile={fileId => onDownload(fileId)}
        />
      ) : null}

      {actionState?.error ? (
        <p className={css.slotError}>
          <FormattedMessage id="ProfileDocumentsPanel.saveFailed" />
        </p>
      ) : null}

      {!item && !disabled ? (
        <label className={css.addFile} htmlFor={`document-${key}`}>
          <input
            id={`document-${key}`}
            ref={inputRef}
            type="file"
            accept={ACCEPT_DOCUMENTS}
            className={css.hiddenInput}
            disabled={isBusy}
            onChange={handleChange}
          />
          <FormattedMessage id="ProfileDocumentsPanel.addFile" />
        </label>
      ) : null}
    </li>
  );
};

/**
 * Vetting / onboarding documents, stored as privateFileAttachments on the user.
 * Visible to the member and to operators only — never to other users.
 */
const ProfileDocumentsPanel = props => {
  const {
    rootClassName,
    className,
    documents,
    slots,
    attachments,
    fileUploads,
    fileUploadsDisabled,
    documentActions,
    missingRequired,
    onUploadDocument,
    onRemoveDocument,
    onClearUpload,
    onDownloadDocument,
  } = props;

  if (!documents?.length) {
    return null;
  }

  // fileId -> file entity, so a slot can resolve its persisted file.
  const filesByUuid = (attachments || [])
    .filter(a => a.attributes?.deleted !== true && a.file)
    .reduce((acc, a) => ({ ...acc, [a.file.id.uuid]: a.file }), {});

  const uploadsByKey = (fileUploads || []).reduce(
    (acc, u) => (u.documentKey ? { ...acc, [u.documentKey]: u } : acc),
    {}
  );

  const handleSelectFile = (file, documentKey) => {
    const tempId = `${documentKey}-${file.name}-${Date.now()}`;
    onUploadDocument(file, tempId, documentKey);
  };

  const handleRemove = (documentKey, isPendingUpload) => {
    if (isPendingUpload) {
      const upload = uploadsByKey[documentKey];
      onClearUpload([upload.tempId]);
    } else {
      onRemoveDocument(documentKey);
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <section className={classes}>
      <H4 as="h2" className={css.title}>
        <FormattedMessage id="ProfileDocumentsPanel.title" />
      </H4>
      <p className={css.subtitle}>
        <FormattedMessage id="ProfileDocumentsPanel.subtitle" />
      </p>

      {fileUploadsDisabled ? (
        <p className={css.disabledNotice}>
          <FormattedMessage id="ProfileDocumentsPanel.uploadsDisabled" />
        </p>
      ) : null}

      <ul className={css.slots}>
        {documents.map(document => (
          <DocumentSlot
            key={document.key}
            document={document}
            attachedFile={filesByUuid[slots?.[document.key]]}
            upload={uploadsByKey[document.key]}
            actionState={documentActions?.[document.key]}
            disabled={fileUploadsDisabled}
            onSelectFile={handleSelectFile}
            onRemove={handleRemove}
            onDownload={onDownloadDocument}
          />
        ))}
      </ul>

      {missingRequired?.length ? (
        <p className={css.missingSummary}>
          <FormattedMessage
            id="ProfileDocumentsPanel.missingRequired"
            values={{
              count: missingRequired.length,
              documents: missingRequired.map(d => d.label).join(', '),
            }}
          />
        </p>
      ) : (
        <p className={css.completeSummary}>
          <FormattedMessage id="ProfileDocumentsPanel.allProvided" />
        </p>
      )}
    </section>
  );
};

export default ProfileDocumentsPanel;
